/**
 * Edit mode of a saved text (handoff §15, §19.7): title, body, gaps, card-word queue and
 * deck links in one place, with the effect of a body edit on the gaps shown live and
 * everything saved in one request.
 *
 * Phone: a full-screen frame with «Текст | Пропуски | Колоды», the change strip pinned
 * above the keyboard and «Готово» at the strip's right. Desktop: the text on the left,
 * the edit controls («Пропуски | Колоды») in the right column, as on the desktop text
 * screen.
 *
 * The draft lives in `textEditStore`; nothing reaches the server before the save. The
 * client never searches the body for a gap: every underline is the server's preview,
 * shifted to follow the typing.
 *
 * Card marks (ADR-115 Amendment 3) are made in the picker and listed on «Слова»
 * with the receiving deck, always the learner's choice (Amendment 4). The save sheet
 * shows the server's budget check for them; the save (`useTextEditSave`) lands on the
 * text screen with the flow save's toast and generation status. Edit mode is the one
 * way to mark words (Amendment 5).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { X } from 'lucide-react'
import { useLocale } from '../i18n/context'
import { useIsDesktop } from '../hooks/useIsDesktop'
import { useEditPreview, useText, useTextLimits, type AppliedPreview } from '../hooks/useTexts'
import { useTextEditSave } from '../hooks/useTextEditSave'
import { useTextEditStore, type EditTab } from '../stores/textEditStore'
import { CONTROL_SURFACE, suppressTouchContextMenu } from '../utils/controlSurface'
import {
  deriveStrip, draftLinks, isDirty, listChanges, shiftSpans, type EditDraft, type Strip,
} from '../utils/textEdit'
import type { TextDetail } from '../types'
import ChangeStrip from '../components/texts/edit/ChangeStrip'
import DecksTab from '../components/texts/edit/DecksTab'
import { EditCardsSummary, NewCardsSection } from '../components/texts/edit/EditCards'
import EditBodyField from '../components/texts/edit/EditBodyField'
import GapCard from '../components/texts/edit/GapCard'
import SaveSheet from '../components/texts/edit/SaveSheet'
import FlowFrame from '../components/texts/FlowFrame'
import LeaveConfirm from '../components/texts/LeaveConfirm'
import { Eyebrow, GhostButton, PrimaryButton, SecondaryPill, Segmented } from '../components/texts/ui'

interface RouteState {
  from?: string
  fromLabel?: string
}

export default function TextEditPage() {
  const { textId } = useParams<{ textId: string }>()
  const { data: text, isLoading, isFetching, isError, error, refetch } = useText(textId)
  const { tr } = useLocale()
  const begin = useTextEditStore((s) => s.begin)
  const draft = useTextEditStore((s) => (s.textId === textId ? s.draft : null))

  // One draft per visit, begun from the settled read. A later change of the cached text (the
  // save writes the new one in) must not begin another after the draft was cleared.
  const begunFor = useRef<string | null>(null)
  useEffect(() => {
    if (!text || isFetching || begunFor.current === text.id) return
    begunFor.current = text.id
    begin(text)
  }, [text, isFetching, begin])

  if (isLoading) return <div aria-busy="true" style={{ minHeight: 240 }} />
  if ((error as { response?: { status?: number } } | null)?.response?.status === 404) {
    return <Navigate to="/library" replace />
  }
  if (isError || !text) {
    return (
      <div role="alert" style={{ textAlign: 'center', padding: '64px 16px', fontFamily: 'var(--font-ui)' }}>
        <p style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{tr.textScreen.loadFailed}</p>
        <SecondaryPill onClick={() => refetch()}>{tr.textScreen.retry}</SecondaryPill>
      </div>
    )
  }
  if (!draft) return <div aria-busy="true" style={{ minHeight: 240 }} />
  return <EditScreen text={text} draft={draft} />
}

const sameForms = (a: string[], b: string[]) => a.length === b.length && a.every((f, i) => f === b[i])

function EditScreen({ text, draft }: { text: TextDetail; draft: EditDraft }) {
  const { tr } = useLocale()
  const te = tr.textEdit
  const navigate = useNavigate()
  const location = useLocation()
  const isDesktop = useIsDesktop()
  const store = useTextEditStore()
  const tab = store.tab
  const limits = useTextLimits().data
  const [saving, setSaving] = useState(false)
  const [discarding, setDiscarding] = useState(false)
  const route = (location.state as RouteState | null) ?? {}

  const seed = useMemo<AppliedPreview>(() => ({
    body: text.body,
    forms: text.gaps.map((g) => g.form),
    gaps: text.gaps.map((g) => ({
      form: g.form, status: 'kept', occurrence_count: g.occurrences.length, occurrences: g.occurrences,
    })),
  }), [text])

  const emptyBody = draft.body.trim() === ''
  const preview = useEditPreview(text.id, draft.body, draft.forms, seed, !emptyBody)
  const { current, previous } = preview
  const live = useMemo(() => current.gaps.filter((g) => g.status !== 'removed'), [current])
  const settled = current.body === draft.body && sameForms(current.forms, draft.forms)
  const dirty = isDirty(text, draft)

  const spans = useMemo(
    () => shiftSpans(live.flatMap((g) => g.occurrences), current.body, draft.body),
    [live, current, draft.body],
  )

  const strip: Strip | null = emptyBody ? { kind: 'empty' } : deriveStrip(previous?.gaps ?? null, current.gaps, draft.forms)
  const undoStrip = () => { if (previous) store.setBody(previous.body) }

  // ── Leaving ───────────────────────────────────────────────────────────────
  const leave = useCallback(() => {
    store.reset()
    navigate(`/texts/${text.id}`, { replace: true, state: { from: route.from, fromLabel: route.fromLabel } })
  }, [store, navigate, text.id, route.from, route.fromLabel])
  const close = () => (dirty ? setDiscarding(true) : leave())

  // ── Saving ────────────────────────────────────────────────────────────────
  const canFinish = !emptyBody && (!dirty || (settled && preview.error === null))
  const finish = () => {
    if (!canFinish) return
    if (dirty) setSaving(true)
    else leave()
  }
  const changes = useMemo(
    () => listChanges(text, draft, current.gaps),
    [text, draft, current.gaps],
  )
  const saver = useTextEditSave({
    text, draft, gaps: current.gaps, sheetOpen: saving, closeSheet: () => setSaving(false), route,
  })

  // ── Pieces ────────────────────────────────────────────────────────────────
  const previewNote = (() => {
    if (preview.error === null) return null
    const code = preview.error
    const text422 =
      code === 'TEXT_TOO_LONG' ? te.tooLong(limits?.max_chars ?? 0)
      : code === 'TEXT_TOO_MANY_GAPS' ? te.tooMany(limits?.max_gaps ?? 0)
      : te.previewFailed
    return (
      <div role="alert" data-testid="edit-preview-error" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, lineHeight: 1.4, color: 'var(--text-secondary)' }}>
        <span style={{ flex: 1 }}>{text422}</span>
        {code !== 'TEXT_TOO_LONG' && code !== 'TEXT_TOO_MANY_GAPS' && (
          <GhostButton onClick={preview.retry} style={{ flexShrink: 0, fontSize: 13 }}>{te.retry}</GhostButton>
        )}
      </div>
    )
  })()

  const textTab = (
    <div data-testid="edit-text-tab" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <Eyebrow>{te.titleField}</Eyebrow>
        <input
          type="text"
          value={draft.title}
          onChange={(e) => store.setTitle(e.target.value)}
          data-testid="edit-title"
          style={{
            height: 44, boxSizing: 'border-box', padding: '0 12px', borderRadius: 8, border: '1px solid var(--border)',
            background: 'var(--bg-surface)', color: 'var(--text-primary)', fontFamily: 'var(--font-ui)', fontSize: 15,
          }}
        />
      </label>
      <EditBodyField value={draft.body} onChange={store.setBody} spans={spans} label={te.bodyField} />
      {previewNote}
      {isDesktop && strip && <ChangeStrip strip={strip} onUndo={undoStrip} />}
    </div>
  )

  const gapsTab = (
    <div data-testid="edit-gaps-tab" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <SecondaryPill full onClick={() => navigate(`/texts/${text.id}/edit/words`, { state: route })} data-testid="edit-mark-words">
        {te.markWords}
      </SecondaryPill>
      <NewCardsSection draft={draft} />
      {live.length > 0 ? (
        <>
          <p style={{ margin: 0, fontSize: 12, color: 'var(--text-tertiary)' }}>{te.inTextOrder}</p>
          {live.map((g) => (
            <GapCard
              key={g.form}
              textId={text.id}
              form={g.form}
              gap={text.gaps.find((s) => s.form === g.form) ?? null}
              places={g.occurrence_count}
              draft={draft}
            />
          ))}
        </>
      ) : (
        <p data-testid="edit-no-gaps" style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary)' }}>{te.noGaps}</p>
      )}
      <WaitingWords text={text} draft={draft} />
    </div>
  )

  const decksTab = <DecksTab draft={draft} />
  const links = draftLinks(draft).length

  const tabs = (
    <Segmented<EditTab>
      label={te.tabsLabel}
      value={tab}
      onChange={store.setTab}
      options={[
        { id: 'text', label: te.tabText, testId: 'edit-tab-text' },
        { id: 'gaps', label: te.tabWords, testId: 'edit-tab-gaps' },
        { id: 'decks', label: te.tabDecks(links), testId: 'edit-tab-decks' },
      ]}
    />
  )

  const done = (
    <GhostButton
      onClick={finish}
      disabled={!canFinish}
      data-testid="edit-done"
      style={{ fontSize: 14, fontWeight: 600, opacity: canFinish ? 1 : 0.4 }}
    >
      {te.done}
    </GhostButton>
  )

  const overlays = (
    <>
      {saving && (
        <SaveSheet
          changes={changes}
          pending={saver.pending}
          onSave={saver.save}
          onBack={() => setSaving(false)}
          blocked={saver.blocked}
          cards={saver.hasCards && <EditCardsSummary draft={draft} check={saver.check} failed={saver.checkFailed} />}
        />
      )}
      <LeaveConfirm
        open={discarding}
        onStay={() => setDiscarding(false)}
        onLeave={leave}
        destructive
        copy={{ title: te.discardTitle, body: te.discardBody, leave: te.discardConfirm, stay: te.discardStay }}
      />
    </>
  )

  if (isDesktop) {
    const side: EditTab = tab === 'decks' ? 'decks' : 'gaps'
    return (
      <>
        <FlowFrame
          testId="text-edit-screen"
          title={te.screenTitle}
          leading="close"
          onLeave={close}
          backLabel={te.close}
          subtitle={text.title}
          maxWidth={1100}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 340px', gap: 24, padding: '24px 0', alignItems: 'start' }}>
            {textTab}
            <aside style={{ position: 'sticky', top: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Segmented<EditTab>
                label={te.tabsLabel}
                value={side}
                onChange={store.setTab}
                options={[
                  { id: 'gaps', label: te.tabWords, testId: 'edit-tab-gaps' },
                  { id: 'decks', label: te.tabDecks(links), testId: 'edit-tab-decks' },
                ]}
              />
              <div style={{ maxHeight: 'calc(100vh - 260px)', overflowY: 'auto' }}>
                {side === 'gaps' ? gapsTab : decksTab}
              </div>
              <PrimaryButton full height={48} onClick={finish} disabled={!canFinish} data-testid="edit-done">{te.done}</PrimaryButton>
            </aside>
          </div>
        </FlowFrame>
        {overlays}
      </>
    )
  }

  return (
    <>
      <FlowFrame
        testId="text-edit-screen"
        title={te.screenTitle}
        leading="close"
        onLeave={close}
        backLabel={te.close}
        subtitle={text.title}
        trailing={done}
        maxWidth={720}
        footer={tab === 'text' && strip ? <ChangeStrip strip={strip} onUndo={undoStrip} bar /> : undefined}
        footerBare={tab === 'text' && !!strip}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '12px 16px 24px' }}>
          {tabs}
          {tab === 'text' ? textTab : tab === 'gaps' ? gapsTab : decksTab}
        </div>
      </FlowFrame>
      {overlays}
    </>
  )
}

/** Card words that did not fit today's limit: × drops one from the queue (an unmarked word). */
function WaitingWords({ text, draft }: { text: TextDetail; draft: EditDraft }) {
  const { tr } = useLocale()
  const te = tr.textEdit
  const store = useTextEditStore()
  const words = text.waiting_words.filter((w) => !draft.dropWaiting.includes(w.id))
  if (words.length === 0) return null
  return (
    <section
      data-testid="edit-waiting"
      className={CONTROL_SURFACE}
      onContextMenu={suppressTouchContextMenu}
      style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 14px', borderRadius: 16, background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
    >
      <Eyebrow>{te.waitingEyebrow}</Eyebrow>
      <p style={{ margin: 0, fontSize: 12, lineHeight: 1.45, color: 'var(--text-secondary)' }}>{te.waitingHint}</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {words.map((w) => (
          <span
            key={w.id}
            data-testid="edit-waiting-word"
            style={{
              display: 'inline-flex', alignItems: 'center', height: 36, paddingLeft: 12, borderRadius: 'var(--radius-pill)',
              border: '1px solid var(--border)', fontFamily: 'var(--font-content)', fontSize: 15, color: 'var(--text-primary)',
            }}
          >
            {w.word}
            <button
              type="button"
              onClick={() => store.dropWaiting(w.id)}
              aria-label={te.dropWaiting(w.word)}
              style={{ width: 36, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}
            >
              <X size={14} aria-hidden />
            </button>
          </span>
        ))}
      </div>
    </section>
  )
}
